import { useState, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  centavos,
  formatFxRate,
  formatMoney,
  parseMoney,
  roundCash,
  settingsSchema,
  type SettingKey,
  type Settings,
} from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import {
  DIAS_VENCIMIENTO,
  HORAS_EQUIPO_CALLADO,
  MARGEN_MAXIMO,
  MINUTOS_BLOQUEO,
  PASOS_REDONDEO,
  TASA_MAXIMA,
  TASA_MINIMA,
  conActual,
  leerMargen,
  leerTasa,
  verMargen,
} from '../lib/configuracion.js';
import { horasDeAviso } from '../lib/equipos.js';
import { emitir } from '../lib/emitir.js';
import { enPractica } from '../lib/practica.js';
import { datosNegocio, muestraRecibo } from '../lib/recibo.js';
import { usePuede } from '../lib/sesion.js';
import { formatearTelefono, revisarTelefono } from '../lib/telefono.js';
import { useT } from '../i18n/index.js';
import { Campo, Interruptor, Selector } from '../ui/Campo.js';
import { CuentasDeBanco } from '../ui/CuentasDeBanco.js';
import { Chip, Ico, type NombreIcono, type Tinte } from '../ui/Iconos.js';
import { Modal } from '../ui/Modal.js';
import { Pagina, Vacio } from '../ui/Pagina.js';

type T = ReturnType<typeof useT>;

/** The settings that are typed rather than picked, each in a sheet of its own. */
type Escrito =
  | 'negocioNombre'
  | 'negocioDireccion'
  | 'negocioTelefono'
  | 'negocioRuc'
  | 'reciboPie'
  | 'fxTasaTienda'
  | 'fxTasaOficial'
  | 'cajaFondo'
  | 'precioMargenMinimoBp'
  | 'ventaRapidaMaxima'
  | 'faltanteAviso'
  | 'cajaReconteoDesde';

/**
 * «Configuración»: what the owner decides once for the whole shop.
 *
 * Nearly all of it is settings the shop already ran on — the store's dollar rate, the coin step
 * cash is rounded to, the margin below which a sale needs the owner — with nowhere to change
 * them, so every shop ran on the defaults. The rest is what a receipt says about the shop, and
 * the two PIN rules. A setting that is written down but does nothing yet (IVA, negative stock)
 * is not offered: a switch that changes nothing is a lie told to the one person who trusts the
 * app with the books.
 *
 * Each change is a `config.cambiada` like any other fact: it applies here at once, reaches every
 * other phone with the next sync, and goes in the bitácora with what it was before. So picking
 * is saving — a switch, a choice — while what is typed gets a sheet with «Guardar», because half
 * a dollar rate must never reach the till.
 *
 * What belongs to one phone alone (its language, its printer's roll) stays in Ajustes.
 */
export function AjustesNegocio() {
  const t = useT();
  const puede = usePuede('configurar');
  const guardada = useLiveQuery(() => readSettings(), [], null);
  const [editando, setEditando] = useState<Escrito | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * What was just picked, shown over the settings it was picked on. The live query answers a
   * moment after the event is written, and in between a switch would flick back to where it was
   * and then over again. Whatever the query answers next is what is in force — this change, or
   * one from another phone that synced in at the same moment — so from then on it shows that.
   */
  const [enCurso, setEnCurso] = useState<{ sobre: Settings; cambios: Partial<Settings> } | null>(null);
  const config =
    guardada && enCurso?.sobre === guardada ? ({ ...guardada, ...enCurso.cambios } as Settings) : guardada;

  if (!puede) {
    return (
      <Pagina titulo={t('negocio.titulo')} volverA="/ajustes">
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  async function guardar(clave: SettingKey, valor: unknown): Promise<boolean> {
    if (!guardada) return false;
    const anterior = guardada[clave];
    if (JSON.stringify(anterior) === JSON.stringify(valor)) return true;
    // The reducer's own rule, checked before the event exists: one it turned down would stay in
    // the log for ever, on every phone, failing on every replay.
    if (!settingsSchema.shape[clave].safeParse(valor).success) {
      setError(t('negocio.noSeGuardo'));
      return false;
    }
    setError(null);
    setEnCurso((antes) => ({
      sobre: guardada,
      cambios: { ...(antes?.sobre === guardada ? antes.cambios : {}), [clave]: valor },
    }));
    try {
      await emitir('config.cambiada', { clave, valor, anterior });
      return true;
    } catch (e) {
      setEnCurso(null);
      setError(e instanceof Error ? e.message : t('negocio.noSeGuardo'));
      return false;
    }
  }

  const minutos = (n: number) =>
    n === 1 ? t('negocio.unMinuto') : n === 60 ? t('negocio.unaHora') : t('negocio.minutos', { n });

  return (
    <Pagina titulo={t('negocio.titulo')} subtitulo={t('negocio.subtitulo')} volverA="/ajustes">
      {error && (
        <p role="alert" className="mb-4 text-sm text-rose-400">
          {error}
        </p>
      )}

      {config && (
        <div className="flex flex-col gap-4">
          <Grupo
            icono="negocio"
            tinte="marca"
            titulo={t('negocio.grupoNegocio')}
            ayuda={t('negocio.grupoNegocioAyuda')}
          >
            <FilaEscrita
              etiqueta={t('negocio.nombre')}
              valor={config.negocioNombre}
              onCambiar={() => setEditando('negocioNombre')}
            />
            <FilaEscrita
              etiqueta={t('negocio.direccion')}
              valor={config.negocioDireccion}
              onCambiar={() => setEditando('negocioDireccion')}
            />
            <FilaEscrita
              etiqueta={t('negocio.telefono')}
              valor={config.negocioTelefono}
              onCambiar={() => setEditando('negocioTelefono')}
            />
            <FilaEscrita
              etiqueta={t('negocio.ruc')}
              valor={config.negocioRuc}
              onCambiar={() => setEditando('negocioRuc')}
            />
            <FilaEscrita
              etiqueta={t('negocio.pie')}
              valor={config.reciboPie}
              onCambiar={() => setEditando('reciboPie')}
            />
            <Muestra texto={muestraRecibo(datosNegocio(config))} />
          </Grupo>

          <Grupo icono="dolares" tinte="esmeralda" titulo={t('negocio.grupoDolares')}>
            <FilaEscrita
              etiqueta={t('negocio.tasaTienda')}
              valor={t('negocio.porDolar', { tasa: formatFxRate(config.fxTasaTienda) })}
              ayuda={t('negocio.tasaTiendaAyuda')}
              onCambiar={() => setEditando('fxTasaTienda')}
            />
            <FilaEscrita
              etiqueta={t('negocio.tasaOficial')}
              valor={t('negocio.porDolar', { tasa: formatFxRate(config.fxTasaOficial) })}
              ayuda={t('negocio.tasaOficialAyuda')}
              onCambiar={() => setEditando('fxTasaOficial')}
            />
          </Grupo>

          <Grupo icono="banco" tinte="cielo" titulo={t('bancos.grupo')} ayuda={t('bancos.grupoAyuda')}>
            <CuentasDeBanco config={config} guardar={guardar} />
          </Grupo>

          <Grupo icono="caja" tinte="ambar" titulo={t('negocio.grupoCaja')}>
            <Redondeo
              paso={config.cajaRedondeoCentavos}
              onElegir={(paso) => void guardar('cajaRedondeoCentavos', paso)}
            />
            <div className="py-2">
              <Interruptor
                etiqueta={t('negocio.conteoCiego')}
                descripcion={t('negocio.conteoCiegoAyuda')}
                checked={config.cajaConteoCiego}
                onChange={(v) => void guardar('cajaConteoCiego', v)}
              />
            </div>
            {/* Each sale only with what is free, and with a connection the server's yes (2026-10-08). */}
            <div className="py-2">
              <Interruptor
                etiqueta={t('negocio.venderSoloLoQueHay')}
                descripcion={t('negocio.venderSoloLoQueHayAyuda')}
                checked={config.venderSoloLoQueHay}
                onChange={(v) => void guardar('venderSoloLoQueHay', v)}
              />
            </div>
            <FilaEscrita
              etiqueta={t('negocio.fondo')}
              valor={
                config.cajaFondo === null ? t('negocio.fondoLibre') : formatMoney(centavos(config.cajaFondo))
              }
              ayuda={t('negocio.fondoAyuda')}
              onCambiar={() => setEditando('cajaFondo')}
            />
            <FilaEscrita
              etiqueta={t('negocio.faltanteAviso')}
              valor={
                config.faltanteAviso === 0
                  ? t('negocio.faltanteAvisoSiempre')
                  : t('negocio.faltanteAvisoDesde', { monto: formatMoney(centavos(config.faltanteAviso)) })
              }
              ayuda={t('negocio.faltanteAvisoAyuda')}
              onCambiar={() => setEditando('faltanteAviso')}
            />
            <FilaEscrita
              etiqueta={t('negocio.reconteo')}
              valor={
                config.cajaReconteoDesde === 0
                  ? t('negocio.reconteoSiempre')
                  : t('negocio.reconteoDesde', { monto: formatMoney(centavos(config.cajaReconteoDesde)) })
              }
              ayuda={t('negocio.reconteoAyuda')}
              onCambiar={() => setEditando('cajaReconteoDesde')}
            />
          </Grupo>

          <Grupo icono="precios" tinte="cielo" titulo={t('negocio.grupoPrecios')}>
            <FilaEscrita
              etiqueta={t('negocio.margenMinimo')}
              valor={verMargen(config.precioMargenMinimoBp)}
              ayuda={t('negocio.margenMinimoAyuda')}
              onCambiar={() => setEditando('precioMargenMinimoBp')}
            />
            <FilaEscrita
              etiqueta={t('negocio.ventaRapida')}
              valor={
                config.ventaRapidaMaxima === 0
                  ? t('negocio.ventaRapidaSiempre')
                  : t('negocio.ventaRapidaHasta', { monto: formatMoney(centavos(config.ventaRapidaMaxima)) })
              }
              ayuda={t('negocio.ventaRapidaAyuda')}
              onCambiar={() => setEditando('ventaRapidaMaxima')}
            />
          </Grupo>

          {/* An order sets aside what it takes, only with the server's yes; the till asks the owner to sell it (2026-10-08). */}
          <Grupo icono="pedidos" tinte="marca" titulo={t('negocio.grupoPedidos')}>
            <div className="py-2">
              <Interruptor
                etiqueta={t('negocio.apartarPedidos')}
                descripcion={t('negocio.apartarPedidosAyuda')}
                checked={config.apartarPedidos}
                onChange={(v) => void guardar('apartarPedidos', v)}
              />
            </div>
          </Grupo>

          {/* Vacations accrue for the whole shop, or not (the owner, 2026-10-31): off, no run sets them aside. */}
          <Grupo icono="personal" tinte="violeta" titulo={t('negocio.grupoPlanilla')}>
            <div className="py-2">
              <Interruptor
                etiqueta={t('negocio.vacaciones')}
                descripcion={t('negocio.vacacionesAyuda')}
                checked={config.vacacionesAcumulan}
                onChange={(v) => void guardar('vacacionesAcumulan', v)}
              />
            </div>
          </Grupo>

          <Grupo icono="bloquear" tinte="rosa" titulo={t('negocio.grupoSeguridad')}>
            <div className="py-3">
              <Selector
                etiqueta={t('negocio.bloqueo')}
                ayuda={
                  enPractica()
                    ? t('negocio.bloqueoPractica')
                    : t('negocio.bloqueoAyuda', { boton: t('inicio.bloquear') })
                }
                value={config.bloqueoMinutos}
                onChange={(e) => void guardar('bloqueoMinutos', Number(e.target.value))}
              >
                {conActual(MINUTOS_BLOQUEO, config.bloqueoMinutos).map((n) => (
                  <option key={n} value={n}>
                    {minutos(n)}
                  </option>
                ))}
              </Selector>
            </div>
            <div className="py-2">
              <Interruptor
                etiqueta={t('negocio.anularConDueno')}
                descripcion={t('negocio.anularConDuenoAyuda')}
                checked={config.anularConDueno}
                onChange={(v) => void guardar('anularConDueno', v)}
              />
            </div>
            <div className="py-3">
              <Selector
                etiqueta={t('negocio.equipoCallado')}
                ayuda={t('negocio.equipoCalladoAyuda')}
                value={horasDeAviso(config.equipoCalladoHoras)}
                onChange={(e) => void guardar('equipoCalladoHoras', Number(e.target.value))}
              >
                {/* In days: under one, picked when hours were offered, reads as one (`horasDeAviso`). */}
                {conActual(HORAS_EQUIPO_CALLADO, horasDeAviso(config.equipoCalladoHoras)).map((n) => (
                  <option key={n} value={n}>
                    {n % 24 !== 0 ? t('negocio.horas', { n }) : n === 24 ? t('negocio.unDia') : t('negocio.dias', { n: n / 24 })}
                  </option>
                ))}
              </Selector>
            </div>
          </Grupo>

          <Grupo icono="vencimientos" tinte="violeta" titulo={t('negocio.grupoVencimientos')}>
            <div className="py-3">
              <Selector
                etiqueta={t('negocio.vencimientoAlerta')}
                ayuda={t('negocio.vencimientoAlertaAyuda', { pantalla: t('venc.titulo') })}
                value={config.vencimientoAlertaDias}
                onChange={(e) => void guardar('vencimientoAlertaDias', Number(e.target.value))}
              >
                {conActual(DIAS_VENCIMIENTO, config.vencimientoAlertaDias).map((n) => (
                  <option key={n} value={n}>
                    {t('negocio.dias', { n })}
                  </option>
                ))}
              </Selector>
            </div>
          </Grupo>
        </div>
      )}

      {editando && config && (
        <CambiarDato
          hoja={hojaDe(editando, config, t)}
          onGuardar={(valor) => guardar(editando, valor)}
          onCerrar={() => setEditando(null)}
        />
      )}
    </Pagina>
  );
}

function Grupo({
  icono,
  tinte,
  titulo,
  ayuda,
  children,
}: {
  icono: NombreIcono;
  tinte: Tinte;
  titulo: string;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <section className="tarjeta">
      <header className="mb-2 flex items-center gap-3">
        <Chip icono={icono} tinte={tinte} />
        <div className="min-w-0">
          <h2 className="font-semibold">{titulo}</h2>
          {ayuda && <p className="text-xs text-slate-500">{ayuda}</p>}
        </div>
      </header>
      <div className="flex flex-col divide-y divide-slate-800">{children}</div>
    </section>
  );
}

/**
 * A setting that is typed: what it is now, and the way in to change it. The whole row is the
 * button, because on a phone the value is where the thumb lands.
 */
function FilaEscrita({
  etiqueta,
  valor,
  ayuda,
  onCambiar,
}: {
  etiqueta: string;
  valor: string;
  ayuda?: string;
  onCambiar: () => void;
}) {
  const t = useT();
  return (
    <div className="py-2">
      <button
        type="button"
        onClick={onCambiar}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-1.5 text-left transition hover:bg-white/5 active:scale-[0.99]"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-slate-400">{etiqueta}</span>
          <span className={`block break-words ${valor ? 'font-medium' : 'text-slate-500'}`}>
            {valor || t('negocio.sinPoner')}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5 text-sm text-slate-400">
          <Ico icono="editar" className="size-4" />
          {t('negocio.cambiar')}
        </span>
      </button>
      {ayuda && <p className="mt-1 text-xs text-slate-500">{ayuda}</p>}
    </div>
  );
}

/** The coin step, as four buttons: every choice readable without opening anything. */
function Redondeo({ paso, onElegir }: { paso: number; onElegir: (paso: number) => void }) {
  const t = useT();
  const ejemplo = centavos(4_730);
  return (
    <div className="flex flex-col gap-2 py-3">
      <span className="etiqueta mb-0" id="negocio-redondeo">
        {t('negocio.redondeo')}
      </span>
      <div role="radiogroup" aria-labelledby="negocio-redondeo" className="flex gap-1 rounded-xl bg-slate-800 p-1">
        {conActual(PASOS_REDONDEO, paso).map((p) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={p === paso}
            onClick={() => onElegir(p)}
            data-sin-icono="pestana"
            className={`tabular flex min-h-9 min-w-0 flex-1 items-center justify-center rounded-lg px-1 text-sm whitespace-nowrap transition ${
              p === paso ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
            }`}
          >
            {/* Under 360px four «C$ 0.25» do not fit side by side; the example below says C$. */}
            {p <= 1 ? (
              t('negocio.exacto')
            ) : (
              <>
                <span className="mr-1 max-[359px]:hidden">C$</span>
                {formatMoney(centavos(p), { symbol: false })}
              </>
            )}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500">
        {paso <= 1
          ? t('negocio.redondeoExacto')
          : t('negocio.redondeoEjemplo', {
              antes: formatMoney(ejemplo),
              despues: formatMoney(roundCash(ejemplo, paso)),
            })}
      </p>
    </div>
  );
}

/**
 * The receipt as it will print, from the very code that prints it: the owner sees an address run
 * onto two lines here, not on the first customer's receipt.
 */
function Muestra({ texto }: { texto: string }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-2 py-3">
      <span className="etiqueta mb-0">{t('negocio.muestra')}</span>
      <pre className="self-center rounded-lg bg-white px-3 py-3 font-mono text-[10px] leading-snug text-slate-900 shadow-inner min-[360px]:text-[11px]">
        {texto}
      </pre>
    </div>
  );
}

/** Everything a sheet needs to take one typed setting. */
interface Hoja {
  titulo: string;
  etiqueta: string;
  ayuda: string;
  inicial: string;
  inputMode?: 'text' | 'decimal' | 'tel';
  maxLength?: number;
  placeholder?: string;
  /** Applied as it is typed, the way the phone field groups its digits. */
  formatear?: (texto: string) => string;
  /** What to save, or what is wrong with what was typed. */
  leer: (texto: string) => { valor: unknown } | { error: string };
  /** Worth knowing about what was typed, without stopping it from being saved. */
  aviso?: (texto: string) => string | null;
}

/** Words, trimmed and with their inner spaces tidied; empty is allowed unless it is not. */
const escrito =
  (t: T, obligatorio = false) =>
  (texto: string) => {
    const limpio = texto.trim().replace(/\s+/g, ' ');
    return obligatorio && !limpio ? { error: t('negocio.errorFalta') } : { valor: limpio };
  };

function hojaDe(clave: Escrito, c: Settings, t: T): Hoja {
  switch (clave) {
    case 'negocioNombre':
      return {
        titulo: t('negocio.nombre'),
        etiqueta: t('negocio.nombre'),
        ayuda: t('negocio.nombreAyuda'),
        inicial: c.negocioNombre,
        maxLength: 60,
        leer: escrito(t, true),
      };
    case 'negocioDireccion':
      return {
        titulo: t('negocio.direccion'),
        etiqueta: t('negocio.direccion'),
        ayuda: t('negocio.direccionAyuda'),
        inicial: c.negocioDireccion,
        maxLength: 120,
        placeholder: t('negocio.direccionEjemplo'),
        leer: escrito(t),
      };
    case 'negocioTelefono':
      return {
        titulo: t('negocio.telefono'),
        etiqueta: t('negocio.telefono'),
        ayuda: t('negocio.telefonoAyuda'),
        inicial: c.negocioTelefono,
        inputMode: 'tel',
        placeholder: '8888 8888',
        formatear: formatearTelefono,
        leer: (texto) => {
          const e = revisarTelefono(texto);
          if (e.tipo === 'vacio') return { valor: '' };
          if (e.tipo === 'valido') return { valor: e.guardar };
          if (e.tipo === 'invalido') {
            return { error: t(e.motivo === 'prefijo' ? 'clientes.telefonoPrefijo' : 'clientes.telefonoLargo') };
          }
          return {
            error: e.extranjero
              ? t('clientes.telefonoExtranjeroCorto')
              : t('clientes.telefonoIncompleto', { n: e.faltan }),
          };
        },
      };
    case 'negocioRuc':
      return {
        titulo: t('negocio.ruc'),
        etiqueta: t('negocio.ruc'),
        ayuda: t('negocio.rucAyuda'),
        inicial: c.negocioRuc,
        maxLength: 20,
        formatear: (texto) => texto.toUpperCase(),
        leer: (texto) => {
          const ruc = texto.replace(/\s+/g, '').toUpperCase();
          return /^[0-9A-Z-]*$/.test(ruc) ? { valor: ruc } : { error: t('negocio.errorRuc') };
        },
      };
    case 'reciboPie':
      return {
        titulo: t('negocio.pie'),
        etiqueta: t('negocio.pie'),
        ayuda: t('negocio.pieAyuda'),
        inicial: c.reciboPie,
        maxLength: 120,
        leer: escrito(t),
      };
    case 'fxTasaTienda':
    case 'fxTasaOficial': {
      const leer = (texto: string) => {
        const r = leerTasa(texto);
        if (r.ok) return { valor: r.valor };
        return r.error === 'rango'
          ? { error: t('negocio.errorTasaRango', { min: TASA_MINIMA, max: TASA_MAXIMA }) }
          : { error: t('negocio.errorTasa') };
      };
      return clave === 'fxTasaTienda'
        ? {
            titulo: t('negocio.tasaTienda'),
            etiqueta: t('negocio.cordobasPorDolar'),
            ayuda: t('negocio.tasaTiendaAyuda'),
            inicial: formatFxRate(c.fxTasaTienda),
            inputMode: 'decimal',
            leer,
            aviso: (texto) => {
              const r = leerTasa(texto);
              return r.ok && r.valor > c.fxTasaOficial
                ? t('negocio.avisoTasa', { oficial: formatFxRate(c.fxTasaOficial) })
                : null;
            },
          }
        : {
            titulo: t('negocio.tasaOficial'),
            etiqueta: t('negocio.cordobasPorDolar'),
            ayuda: t('negocio.tasaOficialAyuda'),
            inicial: formatFxRate(c.fxTasaOficial),
            inputMode: 'decimal',
            leer,
          };
    }
    case 'cajaFondo':
      return {
        titulo: t('negocio.fondo'),
        etiqueta: t('negocio.fondoCordobas'),
        ayuda: t('negocio.fondoHojaAyuda'),
        inicial: c.cajaFondo === null ? '' : formatMoney(centavos(c.cajaFondo), { symbol: false }),
        inputMode: 'decimal',
        placeholder: '0.00',
        leer: (texto) => {
          if (!texto.trim()) return { valor: null };
          const monto = parseMoney(texto);
          return monto === null || monto < 0 ? { error: t('caja.montoInvalido') } : { valor: monto };
        },
      };
    case 'precioMargenMinimoBp':
      return {
        titulo: t('negocio.margenMinimo'),
        etiqueta: t('negocio.porcentaje'),
        ayuda: t('negocio.margenMinimoAyuda'),
        inicial: String(c.precioMargenMinimoBp / 100),
        inputMode: 'decimal',
        leer: (texto) => {
          const r = leerMargen(texto);
          return r.ok ? { valor: r.valor } : { error: t('negocio.errorMargen', { max: MARGEN_MAXIMO }) };
        },
      };
    case 'ventaRapidaMaxima':
      return {
        titulo: t('negocio.ventaRapida'),
        etiqueta: t('negocio.ventaRapidaMonto'),
        ayuda: t('negocio.ventaRapidaHojaAyuda'),
        inicial: formatMoney(centavos(c.ventaRapidaMaxima), { symbol: false }),
        inputMode: 'decimal',
        placeholder: '0.00',
        leer: (texto) => {
          const monto = texto.trim() ? parseMoney(texto) : centavos(0);
          return monto === null || monto < 0 ? { error: t('caja.montoInvalido') } : { valor: monto };
        },
      };
    case 'faltanteAviso':
      return {
        titulo: t('negocio.faltanteAviso'),
        etiqueta: t('negocio.faltanteAvisoMonto'),
        ayuda: t('negocio.faltanteAvisoHojaAyuda'),
        inicial: formatMoney(centavos(c.faltanteAviso), { symbol: false }),
        inputMode: 'decimal',
        placeholder: '0.00',
        leer: (texto) => {
          const monto = texto.trim() ? parseMoney(texto) : centavos(0);
          return monto === null || monto < 0 ? { error: t('caja.montoInvalido') } : { valor: monto };
        },
      };
    case 'cajaReconteoDesde':
      return {
        titulo: t('negocio.reconteo'),
        etiqueta: t('negocio.reconteoMonto'),
        ayuda: t('negocio.reconteoHojaAyuda'),
        inicial: formatMoney(centavos(c.cajaReconteoDesde), { symbol: false }),
        inputMode: 'decimal',
        placeholder: '0.00',
        leer: (texto) => {
          const monto = texto.trim() ? parseMoney(texto) : centavos(0);
          return monto === null || monto < 0 ? { error: t('caja.montoInvalido') } : { valor: monto };
        },
      };
  }
}

/**
 * One typed setting, in a sheet: what is there now, ready to be changed, and «Guardar». What is
 * wrong with it is said once «Guardar» has been tried, and from then on as it is fixed.
 */
function CambiarDato({
  hoja,
  onGuardar,
  onCerrar,
}: {
  hoja: Hoja;
  onGuardar: (valor: unknown) => Promise<boolean>;
  onCerrar: () => void;
}) {
  const t = useT();
  const [texto, setTexto] = useState(hoja.inicial);
  const [intentado, setIntentado] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const lectura = hoja.leer(texto);
  const error = intentado && 'error' in lectura ? lectura.error : null;
  const aviso = hoja.aviso?.(texto) ?? null;
  const numerico = hoja.inputMode === 'decimal' || hoja.inputMode === 'tel';

  async function aceptar() {
    setIntentado(true);
    if ('error' in lectura || ocupado) return;
    setOcupado(true);
    if (await onGuardar(lectura.valor)) onCerrar();
    else setOcupado(false);
  }

  return (
    <Modal
      titulo={hoja.titulo}
      onCerrar={onCerrar}
      pie={
        // Side by side down to 320 px: with the buttons' usual padding the two ran 20 px past the sheet's edge.
        <div className="flex gap-3">
          <button type="button" className="btn-secundario flex-1 px-3" onClick={onCerrar}>
            <Ico icono="cancelar" />
            {t('comun.cancelar')}
          </button>
          <button type="button" className="btn-primario flex-1 px-3" disabled={ocupado} onClick={() => void aceptar()}>
            <Ico icono="guardar" />
            {ocupado ? t('comun.guardando') : t('comun.guardar')}
          </button>
        </div>
      }
    >
      <Campo
        etiqueta={hoja.etiqueta}
        ayuda={hoja.ayuda}
        error={error}
        value={texto}
        onChange={(e) => setTexto(hoja.formatear ? hoja.formatear(e.target.value) : e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void aceptar()}
        inputMode={hoja.inputMode ?? 'text'}
        {...(hoja.maxLength ? { maxLength: hoja.maxLength } : {})}
        {...(hoja.placeholder ? { placeholder: hoja.placeholder } : {})}
        className={numerico ? 'tabular text-lg tracking-wide' : undefined}
        autoFocus
        autoComplete="off"
      />
      {aviso && (
        <p className="mt-3 flex items-start gap-2 text-sm text-amber-300">
          <Ico icono="aviso" className="mt-0.5 size-4 shrink-0" />
          {aviso}
        </p>
      )}
    </Modal>
  );
}
